import { ChevronLeft, ChevronRight, ChevronsDown } from "lucide-react";
import classNames from "classnames";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/ui/components/ui/dropdown-menu.tsx";
import { Button } from "@/ui/components/ui/button.tsx";
import { ApiResponseMeta } from "@/shared/types/api.ts";
import { useFilterStore } from "@/shared/stores/useFilterStore.ts";

interface PaginationProps {
  pagination?: ApiResponseMeta;
  filterKey: string;
}

const Pagination = ({ pagination, filterKey }: PaginationProps) => {
  const { t } = useTranslation("common");
  const { setFilters, getFilters } = useFilterStore();
  const [prevPagination, setPrevPagination] = useState<ApiResponseMeta | null>(null);
  const pageSizeOptions = [10,20,50];

  useEffect(() => {
    if (pagination) {
      setPrevPagination(pagination);
    }
  }, [pagination]);

  const activePagination = pagination ?? prevPagination;


  if (!activePagination) return null;

  const { pageCount: totalPages, itemsPerPage, totalItems } = activePagination;
  const filters = getFilters(filterKey);
  const currentPage = filters?.page ?? 1;
  const pageSize = filters?.itemsPerPage ?? itemsPerPage ?? 10;

  const onPageChange = (page: number) => {
    if (page < 1 || page > totalPages) return;
    setFilters(filterKey, { ...getFilters(filterKey), page });
  };

  const onPageSizeChange = (newSize: number) => {
    setFilters(filterKey, { ...getFilters(filterKey), itemsPerPage: newSize, page: 1 });
  };



  return (
    <div className="flex flex-col md:flex-row items-center justify-between gap-4 py-4 px-6">
      <div className="hidden md:flex items-center gap-2">
        <span className="text-sm text-muted-foreground">{t("pagination.rowsPerPage")}</span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="flex items-center gap-1">
              {pageSize}
              <ChevronsDown size={16} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-24">
            {pageSizeOptions.map((size) => (
              <DropdownMenuItem key={size} onSelect={() => onPageSizeChange(size)}>
                {size}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Pagination controls */}
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={currentPage === 1}
          onClick={() => onPageChange(currentPage - 1)}
          className={classNames("flex items-center", {
            "cursor-not-allowed opacity-50": currentPage === 1,
          })}
        >
          <ChevronLeft size={18} />
          <span className="hidden lg:inline">{t("pagination.previous")}</span>
        </Button>
        <span className="text-sm text-muted-foreground">
          {t("pagination.pageOf", { current: currentPage, total: totalPages })}
        </span>
        <Button
          variant="outline"
          size="sm"
          disabled={currentPage === totalPages}
          onClick={() => onPageChange(currentPage + 1)}
          className={classNames("flex items-center", {
            "cursor-not-allowed opacity-50": currentPage === totalPages,
          })}
        >
          <span className="hidden lg:inline">{t("pagination.next")}</span>
          <ChevronRight size={18} />
        </Button>
      </div>
    </div>
  );
};

export default Pagination;
